import {
  CREDIT_CLASSIFICATION_IG,
  CREDIT_CLASSIFICATION_INSURED,
  type AllianzRating,
  type CreditClassification,
  type FitchRating,
  type JcrRating,
  type MoodysRating,
  type SpRating
} from '@klear/constants'
import { getHighestIgRatingWithAgency } from '@klear/credit/credit-rating'
import { Money } from '@klear/money'
import { Timestamp } from '@klear/time'

export function missingRequiredEhid(
  classification: CreditClassification | null | undefined,
  allianzEhid: string | null | undefined
): boolean {
  return classification !== CREDIT_CLASSIFICATION_IG && !allianzEhid?.trim()
}

export type CreditIdentity = { kind: 'new' } | { kind: 'saved'; organizationId: number }

export function creditToJson(credit: Credit) {
  return {
    identity: credit.identity,
    organizationId: credit.organizationId,
    customerId: credit.customerId,
    moodysRating: credit.moodysRating,
    spRating: credit.spRating,
    fitchRating: credit.fitchRating,
    jcrRating: credit.jcrRating,
    allianzRating: credit.allianzRating,
    allianzCreditLimit: credit.allianzCreditLimit.toJSON(),
    allianzRefreshedAt: credit.allianzRefreshedAt?.epochMs,
    creditClassification: credit.creditClassification,
    availableCreditLimit: credit.availableCreditLimit?.toJSON()
  }
}
export type CreditJson = ReturnType<typeof creditToJson>

export class Credit {
  static readonly __klearClass = 'Credit'

  readonly identity: CreditIdentity
  organizationId?: number
  customerId?: number
  moodysRating?: MoodysRating
  spRating?: SpRating
  fitchRating?: FitchRating
  jcrRating?: JcrRating
  allianzRating?: AllianzRating
  allianzCreditLimit: Money = new Money()
  allianzRefreshedAt?: Timestamp
  creditClassification?: CreditClassification
  availableCreditLimit?: Money

  constructor(props: CreditJson) {
    this.identity =
      props.identity?.kind === 'saved'
        ? { kind: 'saved', organizationId: props.identity.organizationId }
        : { kind: 'new' }
    this.organizationId = this.identity.kind === 'saved' ? this.identity.organizationId : props.organizationId
    this.customerId = props.customerId
    this.moodysRating = props.moodysRating
    this.spRating = props.spRating
    this.fitchRating = props.fitchRating
    this.jcrRating = props.jcrRating
    this.allianzRating = props.allianzRating
    this.allianzCreditLimit = new Money(props.allianzCreditLimit)
    this.allianzRefreshedAt = props.allianzRefreshedAt != null ? new Timestamp(props.allianzRefreshedAt) : undefined
    this.creditClassification = props.creditClassification
    this.availableCreditLimit = props.availableCreditLimit != null ? new Money(props.availableCreditLimit) : undefined
    this.assertValid()
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  get hasCreditLimit(): boolean {
    return this.allianzCreditLimit.amount !== 0n
  }

  get displayRating(): string | null {
    const allianz = this.allianzRating == null ? null : `Allianz ${this.allianzRating}`
    if (this.creditClassification === CREDIT_CLASSIFICATION_INSURED) return allianz
    return getHighestIgRatingWithAgency(this)?.rating ?? allianz
  }

  computeUtilization(offerPurchasePrices: Money[]): Money {
    const limit = this.allianzCreditLimit
    const inCurrency = offerPurchasePrices.filter((p) => p.currency === limit.currency)
    return inCurrency.length > 0 ? Money.sum(inCurrency) : limit.zero
  }

  requireSaved(): asserts this is this & { organizationId: number } {
    if (!this.isSaved || this.organizationId == null) {
      throw new Error('Credit is not saved')
    }
  }

  assertValid(): void {
    if (this.identity.kind === 'saved' && this.organizationId == null) {
      throw new Error('Saved credit requires organizationId')
    }
  }

  toJSON() {
    return creditToJson(this)
  }
}
